import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import * as reportService from '../services/reportService';

export function LeadershipReport(): React.ReactElement {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState<reportService.LeadershipReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [projectRequirements, setProjectRequirements] = useState<Record<string, reportService.ProjectRequirement[]>>({});
  const [loadingRequirements, setLoadingRequirements] = useState<Set<string>>(new Set());
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [blockedRequirements, setBlockedRequirements] = useState<reportService.BlockedRequirement[]>([]);
  const [loadingBlocked, setLoadingBlocked] = useState(false);

  // Load report on mount
  useEffect(() => {
    async function loadReport() {
      if (!token) return;
      try {
        setLoading(true);
        setError(null);
        const data = await reportService.getLeadershipReport(token);
        setReportData(data);
      } catch (err: any) {
        console.error('Error loading leadership report:', err);
        setError('Failed to load leadership report');
      } finally {
        setLoading(false);
      }
    }
    loadReport();
  }, [token]);

  const toggleProject = async (projectId: string) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
      // Load requirements if not already loaded
      if (!projectRequirements[projectId] && token) {
        setLoadingRequirements((prev) => new Set(prev).add(projectId));
        try {
          const reqs = await reportService.getProjectRequirements(projectId, token);
          setProjectRequirements((prev) => ({ ...prev, [projectId]: reqs }));
        } catch (err) {
          console.error('Error loading project requirements:', err);
        } finally {
          setLoadingRequirements((prev) => {
            const next = new Set(prev);
            next.delete(projectId);
            return next;
          });
        }
      }
    }
    setExpandedProjects(newExpanded);
  };

  const handleBlockedClick = async () => {
    if (!token) return;
    setShowBlockedModal(true);
    setLoadingBlocked(true);
    try {
      const data = await reportService.getBlockedRequirements(token);
      setBlockedRequirements(data);
    } catch (err) {
      console.error('Error loading blocked requirements:', err);
    } finally {
      setLoadingBlocked(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'approved':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400';
      case 'draft':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'deferred':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'medium':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'low':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getProjectStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'done':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'ready':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'draft':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getApprovalBadgeClass = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getWorkflowBadgeClass = (columnType: string | null) => {
    switch (columnType) {
      case 'todo':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'blocked':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'done':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'done_not_worked':
        return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-2xl font-bold text-white">Leadership Report</h1>
          <p className="text-indigo-100 mt-1">Company-wide view of all projects with requirement status breakdown</p>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        )}

        {/* Report Content */}
        {!loading && reportData && (
          <>
            {/* Company Totals */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Company Overview</h2>

              {/* Summary Stats + Approval Breakdown */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <span className="text-gray-500 dark:text-gray-400 text-sm">Total Projects</span>
                  <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{reportData.totals.total_projects}</p>
                </div>
                <div className="text-right">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">Requirements</span>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{reportData.totals.total_requirements}</p>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-100 dark:bg-green-900/30 text-sm font-semibold text-green-700 dark:text-green-400">
                      {reportData.totals.approved} <span className="font-medium">Approved</span>
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-sm font-semibold text-yellow-700 dark:text-yellow-400">
                      {reportData.totals.pending} <span className="font-medium">Pending</span>
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-100 dark:bg-red-900/30 text-sm font-semibold text-red-700 dark:text-red-400">
                      {reportData.totals.rejected} <span className="font-medium">Rejected</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Sprint Workflow Status Section */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Sprint Workflow Status</h3>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  <div className="text-center p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                    <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                      {reportData.totals.total_requirements - reportData.totals.board_todo - reportData.totals.board_in_progress - reportData.totals.board_blocked - reportData.totals.board_done - reportData.totals.board_done_not_worked}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Backlog</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{reportData.totals.board_todo}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">To Do</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{reportData.totals.board_in_progress}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">In Progress</p>
                  </div>
                  <div
                    onClick={reportData.totals.board_blocked > 0 ? handleBlockedClick : undefined}
                    className={`text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg ${reportData.totals.board_blocked > 0 ? 'cursor-pointer hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors' : ''}`}
                  >
                    <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{reportData.totals.board_blocked}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Blocked</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">{reportData.totals.board_done}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Done</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-600 dark:text-gray-300">{reportData.totals.board_done_not_worked}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Done / Not Worked</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Projects List */}
            <div className="space-y-4">
              {reportData.projects.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No projects found</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Create a project to see it in this report.</p>
                </div>
              ) : (
                reportData.projects.map((project) => (
                  <div key={project.id} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    {/* Project Header */}
                    <button
                      onClick={() => toggleProject(project.id)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <svg
                          className={`w-5 h-5 text-gray-400 transition-transform ${
                            expandedProjects.has(project.id) ? 'rotate-90' : ''
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <div className="text-left">
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                            {project.name}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {project.project_code} • {project.total_requirements} requirements
                          </p>
                          {project.owner_name && (
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              Owner: <span className="text-gray-600 dark:text-gray-400">{project.owner_name}</span>
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        {/* Progress Bar */}
                        <div className="w-32 hidden md:block">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-gray-500 dark:text-gray-400">Progress</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">{project.progress_percent}%</span>
                          </div>
                          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"
                              style={{ width: `${project.progress_percent}%` }}
                            ></div>
                          </div>
                        </div>
                        {/* Status Badges */}
                        <div className="flex gap-2">
                          {project.board_breakdown.todo > 0 && (
                            <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 rounded">
                              {project.board_breakdown.todo} To Do
                            </span>
                          )}
                          {project.board_breakdown.in_progress > 0 && (
                            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 rounded">
                              {project.board_breakdown.in_progress} In Progress
                            </span>
                          )}
                          {project.board_breakdown.blocked > 0 && (
                            <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 rounded">
                              {project.board_breakdown.blocked} Blocked
                            </span>
                          )}
                          {project.board_breakdown.done > 0 && (
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded">
                              {project.board_breakdown.done} Done
                            </span>
                          )}
                        </div>
                      </div>
                    </button>

                    {/* Expanded Content */}
                    {expandedProjects.has(project.id) && (
                      <div className="border-t border-gray-200 dark:border-gray-700">
                        {loadingRequirements.has(project.id) ? (
                          <div className="px-6 py-8 flex justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                              <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Title
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Approval
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Workflow
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Priority
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Assignee
                                  </th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Completed At
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {(projectRequirements[project.id] || []).length === 0 ? (
                                  <tr>
                                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                                      No requirements in this project
                                    </td>
                                  </tr>
                                ) : (
                                  (projectRequirements[project.id] || []).map((req) => (
                                    <tr key={req.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                                        {req.title}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs rounded-full ${getApprovalBadgeClass(req.approval_status)}`}>
                                          {req.approval_status}
                                        </span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        {req.workflow_column_name ? (
                                          <span className={`px-2 py-1 text-xs rounded-full ${getWorkflowBadgeClass(req.workflow_column_type)}`}>
                                            {req.workflow_column_name}
                                          </span>
                                        ) : (
                                          <span className="text-gray-400 dark:text-gray-500 text-xs">-</span>
                                        )}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 text-xs rounded-full ${getPriorityBadgeClass(req.priority)}`}>
                                          {req.priority}
                                        </span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        {req.assigned_to_name || '-'}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        {formatDateTime(req.completed_at)}
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* Blocked Requirements Modal */}
      {showBlockedModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black/60 backdrop-blur-sm" onClick={() => setShowBlockedModal(false)} />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">Blocked Requirements</h3>
                  <button onClick={() => setShowBlockedModal(false)} className="text-white/80 hover:text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="px-6 py-4">
                {loadingBlocked ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                  </div>
                ) : blockedRequirements.length === 0 ? (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-8">No blocked requirements found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead>
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-1/3">Project</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-1/6">Owner</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Requirement</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {blockedRequirements.map((req) => (
                          <tr key={req.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-6 py-3 text-sm text-gray-900 dark:text-gray-100">{req.project_name}</td>
                            <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{req.owner_name || '-'}</td>
                            <td className="px-6 py-3 text-sm text-gray-900 dark:text-gray-100">
                              <div className="flex items-center gap-2">
                                <span>{req.title}</span>
                                <button
                                  onClick={() => { setShowBlockedModal(false); navigate(`/taskboard/requirement/${req.id}`); }}
                                  className="flex-shrink-0 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                                  title="View requirement detail"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-3 flex justify-end">
                <button
                  onClick={() => setShowBlockedModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default LeadershipReport;
